import {
  APICurrentUserConfidentialFieldPermissions,
  APICurrentUserPermission,
  ArdoqId,
} from '@ardoq/api-types';
import {
  AddApiResourcePermissionAttributePayload,
  permissionsOperations,
} from '@ardoq/access-control';
import {
  isEqual,
  isTruthy,
  returnPayload,
  setStateProperty,
} from '@ardoq/common-helpers';
import {
  action$,
  extractPayload,
  ObservableState,
  ofType,
  persistentReducedStream,
  reducer,
  spreadMap,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  pairwise,
  shareReplay,
  startWith,
  withLatestFrom,
} from 'rxjs';
import {
  addPermissionForResource,
  confidentialFieldsReceived,
  fetchedCurrentUserPermissions,
  resetCurrentUserPermissions,
} from './actions';
import { notifyPermissionsChange as notifyGridEditorPermissionsChanged } from 'gridEditor/actions';
import {
  fieldsToWorkspaces$,
  FieldsToWorkspacesMap,
} from '../fields/fieldsToWorkspaces$';
import { affectedWorkspacesByConfidentialFieldPermissionChange } from './affectedWorkspacesByConfidentialFieldPermissionChange';
import { websocket$ } from 'sync/websocket$';
import { ardoqEventOperations } from 'sync/ardoqEventOperations';
import { CurrentUserPermissionsState } from './types';

export const apiConfidentialFieldPermissions$: Observable<APICurrentUserConfidentialFieldPermissions | null> =
  merge(
    action$.pipe(ofType(confidentialFieldsReceived), extractPayload()),
    websocket$.pipe(
      filter(ardoqEventOperations.isFieldAccessContextChanged),
      map(({ data }) => data)
    )
  ).pipe(
    filter(isTruthy),
    distinctUntilChanged(isEqual),
    startWith(null),
    shareReplay({ bufferSize: 1, refCount: true })
  );

/**
 * Overwrite entire permission state with incoming user resource permissions
 * that are expected to be the most up-to-date permissions.
 */
const handleFetchedPermissions = (
  state: CurrentUserPermissionsState,
  permissions: APICurrentUserPermission[]
): CurrentUserPermissionsState => {
  return {
    ...state,
    resourcePermissions:
      permissionsOperations.fromApiUserResourcePermissions(permissions),
  };
};

const handleAddPermissionForResource = (
  state: CurrentUserPermissionsState,
  payload: AddApiResourcePermissionAttributePayload
): CurrentUserPermissionsState => {
  return {
    ...state,
    resourcePermissions:
      permissionsOperations.addApiResourcePermissionAttribute(
        state.resourcePermissions,
        payload
      ),
  };
};

const initialState: CurrentUserPermissionsState = {
  resourcePermissions: {},
  confidentialFieldPermissions: null,
};
const getInitialState = (): CurrentUserPermissionsState => initialState;

const internalCurrentUserPermissions$: ObservableState<CurrentUserPermissionsState> =
  persistentReducedStream<CurrentUserPermissionsState>(
    'currentUserPermissions$',
    initialState,
    [
      reducer(fetchedCurrentUserPermissions, handleFetchedPermissions),
      streamReducer(
        apiConfidentialFieldPermissions$,
        setStateProperty('confidentialFieldPermissions')
      ),
      reducer(addPermissionForResource, handleAddPermissionForResource),
      // GridEditor relies on this stream and is notified about changes from
      // ardoq-front via the PostMessage Bridge.
      reducer(notifyGridEditorPermissionsChanged, returnPayload),
      reducer(resetCurrentUserPermissions, getInitialState),
    ]
  );

/**
 * This is source data for `currentUserPermissionContext$`, which is probably
 * what you're looking for.
 *
 * Exported as normal observable to prevent direct state access
 */
export const currentUserPermissions$ = internalCurrentUserPermissions$.pipe();

export const workspacesAffectedByPermissionChange$: Observable<ArdoqId[]> =
  apiConfidentialFieldPermissions$.pipe(
    filter(isTruthy),
    pairwise(),
    withLatestFrom(fieldsToWorkspaces$),
    map(
      ([[prev, current], fieldsToWorkspaces]): [
        APICurrentUserConfidentialFieldPermissions,
        APICurrentUserConfidentialFieldPermissions,
        FieldsToWorkspacesMap,
      ] => [prev, current, fieldsToWorkspaces]
    ),
    spreadMap(affectedWorkspacesByConfidentialFieldPermissionChange),
    filter(workspaceIds => workspaceIds.length > 0)
  );
